/**
 * Wrapper around supabase.functions.invoke for AI-generation endpoints.
 * - Always attaches an `x-guest-id` header so the server can rate-limit
 *   anonymous callers (5 generations / 6h). Signed-in users are not
 *   rate-limited; the server detects them via the auth token automatically.
 * - Surfaces a friendly toast on 429 rate_limited responses with a CTA
 *   pointing at /auth.
 */

import { supabase } from "@/integrations/supabase/client";
import { getGuestId } from "@/lib/guestId";
import { toast } from "sonner";

export class AIRateLimitError extends Error {
  retryAfterSeconds?: number;
  constructor(message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = "AIRateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

interface InvokeAIOptions {
  body?: unknown;
  /** Show a toast automatically when rate-limited. Default true. */
  toastOnLimit?: boolean;
}

export async function invokeAI<T = unknown>(
  fnName: string,
  { body, toastOnLimit = true }: InvokeAIOptions = {},
): Promise<{ data: T | null; error: any }> {
  const headers: Record<string, string> = {
    "x-guest-id": getGuestId(),
  };

  const { data, error } = await supabase.functions.invoke(fnName, {
    body,
    headers,
  });

  // Edge functions return structured errors in error.context for non-2xx.
  if (error) {
    const ctx: any = (error as any)?.context ?? {};
    // FunctionsHttpError exposes a Response on .context with json() — try to read structured body.
    let payload: any = ctx;
    if (ctx && typeof ctx.json === "function") {
      try {
        payload = await ctx.json();
      } catch { /* ignore */ }
    }
    const code = payload?.error;
    const status = ctx?.status;
    if (code === "rate_limited" || status === 429) {
      const retry = Number(payload?.retry_after_seconds) || 6 * 60 * 60;
      const msg = payload?.message ?? "You've hit the free guest AI limit.";
      if (toastOnLimit) {
        toast.error("Free AI limit reached", {
          description: `${msg} Try again in ${formatRetry(retry)}, or sign up free for unlimited.`,
          duration: 8000,
          action: {
            label: "Sign up free",
            onClick: () => {
              window.location.href = "/auth?mode=signup";
            },
          },
        });
      }
      return {
        data: null,
        error: new AIRateLimitError(msg, retry),
      };
    }
  }

  return { data: data as T | null, error };
}

function formatRetry(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.ceil(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours}h ${rem}m` : `${hours}h`;
}
